import { useState, useCallback } from 'react'
import {
  addFormInputs,
  getAuthToken,
  getDateRangeParams,
  getQueryParam,
  submitExportForm
} from '../utils/exportHelpers'

export const VIEWS = {
  INITIAL: 'initial',
  EXCEL_OPTIONS: 'excel_options',
  EXCEL_CUSTOM: 'excel_custom',
  PDF_OPTIONS: 'pdf_options',
  PHOTOS_OPTIONS: 'photos_options'
}

export const useExportModal = ({
  collect,
  draft,
  exportUrl,
  pdfExportUrl,
  photosExportUrl,
  defaultView,
  currentSectionId,
  ignoreFilters
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [currentView, setCurrentView] = useState(defaultView || VIEWS.INITIAL)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    sections: [],
    questions: [],
    allVariables: true,
    exportAsZip: true
  })

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setCurrentView(defaultView || VIEWS.INITIAL)
  }, [defaultView])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setCurrentView(defaultView || VIEWS.INITIAL)
  }, [defaultView])

  const handleExcelClick = useCallback(() => {
    setCurrentView(VIEWS.EXCEL_OPTIONS)
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const handlePDFClick = useCallback(() => {
    setCurrentView(VIEWS.PDF_OPTIONS)
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const handlePhotosClick = useCallback(() => {
    setCurrentView(VIEWS.PHOTOS_OPTIONS)
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const handlePhotosCustomize = useCallback(() => {
    // Redirect to photos export page for custom selection
    const basePath = photosExportUrl.replace('/photos_exports', '')
    const photosExportPath = `${basePath}/photos_exports`
    const url = new URL(photosExportPath, window.location.origin)
    if (currentSectionId)
      url.searchParams.set('section_id', currentSectionId)
    const sphericalPhotos = getQueryParam('spherical_photos')
    if (sphericalPhotos !== null)
      url.searchParams.set('spherical_photos', sphericalPhotos)
    if (ignoreFilters)
      url.searchParams.set('ignore_filters', 'true')
    Object.entries(getDateRangeParams()).forEach(([key, value]) => {
      if (value !== null)
        url.searchParams.set(key, value)
    })
    window.location.href = url.toString()
  }, [photosExportUrl, currentSectionId, ignoreFilters])

  const handleBack = useCallback(() => {
    switch (currentView) {
    case VIEWS.EXCEL_OPTIONS:
    case VIEWS.PDF_OPTIONS:
    case VIEWS.PHOTOS_OPTIONS:
      setCurrentView(VIEWS.INITIAL)
      break
    case VIEWS.EXCEL_CUSTOM:
      setCurrentView(VIEWS.EXCEL_OPTIONS)
      break
    default:
      setCurrentView(VIEWS.INITIAL)
    }
    setCurrentStep(currentStep - 1)
  }, [currentView, currentStep])

  const handleExport = useCallback(format => {
    setLoading(true)
    const isCustom = currentView === VIEWS.EXCEL_CUSTOM || currentView === VIEWS.PDF_OPTIONS
    const url = format === 'pdf' ? pdfExportUrl : exportUrl

    submitExportForm({
      url,
      format,
      collectId: collect.id,
      draftId: draft?.id,
      ignoreFilters,
      ...isCustom ? {
        sectionIds: formData.sections,
        questionIds: formData.questions.map(question => ({
          id: question.id,
          sectionId: question.sectionId
        })),
        withVariables: formData.allVariables,
        exportAsZip: formData.exportAsZip
      } : {}
    })

    if (window.plausible) {
      const exportType = format === 'pdf' ? 'PDF' : 'Excel'
      window.plausible(`Collect ${exportType} Exported`)
    }
  }, [currentView, exportUrl, formData, pdfExportUrl, collect.id, draft?.id, ignoreFilters])

  const handleExcelExport = useCallback(() => handleExport('xlsx'), [handleExport])
  const handlePDFExport = useCallback(() => handleExport('pdf'), [handleExport])
  const handlePhotosExport = useCallback(() => {
    setLoading(true)
    if (window.plausible)
      window.plausible('Collect Photos Exported')

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = photosExportUrl

    addFormInputs(form, {
      'authenticity_token': getAuthToken() ?? null,
      'export_all': 'true',
      'return_url': window.location.href,
      'section_ids[]': currentSectionId ?? null,
      'spherical_photos': getQueryParam('spherical_photos'),
      'ignore_filters': ignoreFilters ? 'true' : null,
      ...getDateRangeParams()
    })

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)

    setTimeout(() => {
      setLoading(false)
      setIsOpen(false)
    }, 1000)
  }, [photosExportUrl, currentSectionId, ignoreFilters])

  const handleSectionChange = useCallback(selectedSections => {
    setFormData(prev => ({ ...prev, sections: selectedSections }))
  }, [])

  const handleVariablesChange = useCallback(event => {
    setFormData(prev => ({
      ...prev,
      allVariables: event.target.checked
    }))
  }, [])

  const handleExportAsZipChange = useCallback(value => {
    setFormData(prev => ({
      ...prev,
      exportAsZip: value
    }))
  }, [])


  return {
    isOpen,
    currentStep,
    currentView,
    loading,
    formData,
    handleClose,
    handleOpen,
    handleExcelClick,
    handlePDFClick,
    handlePhotosClick,
    handlePhotosCustomize,
    handleBack,
    handleExcelExport,
    handlePDFExport,
    handlePhotosExport,
    handleSectionChange,
    handleVariablesChange,
    handleExportAsZipChange
  }
}
